import { resolveBillingDestinationPath } from '@/lib/navigation/billing-destination';
import { DOCS_URL } from '@/lib/constants/external-urls-constants';
import {
  AccountActionId,
  AccountActionKind,
  AccountActionSection,
  AccountMenuSurface,
  AppearanceThemeId,
} from './types/account-action-catalog-enums';
import {
  GET_STARTED_LABEL,
  GET_STARTED_PATH,
  SIGN_OUT_LABEL,
} from './constants/account-action-catalog-constants';
import type {
  AccountActionCatalogContext,
  AccountActionDefinition,
} from './types/account-action-catalog-types';

const ACCOUNT_SETTINGS_PATH = '/account/settings';
const API_KEYS_PATH = '/account/settings/api-keys';

export function resolveAccountActionCatalog(
  context: AccountActionCatalogContext = {},
): AccountActionDefinition[] {
  const billingHref = resolveBillingDestinationPath({
    isShopifyGateway: context.isShopifyGateway,
  });

  const actions: AccountActionDefinition[] = [
    {
      id: AccountActionId.WORKSPACE_SETTINGS,
      label: 'Workspace settings',
      section: AccountActionSection.WORKSPACE,
      kind: AccountActionKind.LINK,
      surfaces: [
        AccountMenuSurface.DESKTOP_WORKSPACE,
        AccountMenuSurface.DESKTOP_TOPBAR,
        AccountMenuSurface.MOBILE_MORE,
      ],
      href: ACCOUNT_SETTINGS_PATH,
    },
    {
      id: AccountActionId.BILLING,
      label: 'Billing',
      section: AccountActionSection.WORKSPACE,
      kind: AccountActionKind.LINK,
      surfaces: [AccountMenuSurface.DESKTOP_WORKSPACE, AccountMenuSurface.MOBILE_MORE],
      href: billingHref,
    },
    {
      id: AccountActionId.ONBOARDING_SETUP,
      label: GET_STARTED_LABEL,
      section: AccountActionSection.WORKSPACE,
      kind: AccountActionKind.LINK,
      surfaces: [AccountMenuSurface.DESKTOP_WORKSPACE, AccountMenuSurface.MOBILE_MORE],
      href: context.onboardingSetupHref ?? GET_STARTED_PATH,
    },
  ];

  actions.push(
    {
      id: AccountActionId.OPEN_HELP,
      label: 'Help',
      section: AccountActionSection.HELP,
      kind: AccountActionKind.ACTION,
      surfaces: [AccountMenuSurface.DESKTOP_TOPBAR],
    },
    {
      id: AccountActionId.DOCUMENTATION,
      label: 'Documentation',
      section: AccountActionSection.HELP,
      kind: AccountActionKind.EXTERNAL_LINK,
      surfaces: [
        AccountMenuSurface.DESKTOP_HELP,
        AccountMenuSurface.DESKTOP_TOPBAR,
        AccountMenuSurface.MOBILE_MORE,
      ],
      href: DOCS_URL,
    },
    {
      id: AccountActionId.LIVE_CHAT,
      label: 'Support',
      section: AccountActionSection.HELP,
      kind: AccountActionKind.ACTION,
      surfaces: [
        AccountMenuSurface.DESKTOP_HELP,
        AccountMenuSurface.DESKTOP_TOPBAR,
        AccountMenuSurface.MOBILE_MORE,
      ],
    },
    {
      id: AccountActionId.API_KEYS,
      label: 'API Keys',
      section: AccountActionSection.DEVELOPER,
      kind: AccountActionKind.LINK,
      surfaces: [
        AccountMenuSurface.DESKTOP_DEVELOPER,
        AccountMenuSurface.MOBILE_MORE,
      ],
      href: API_KEYS_PATH,
    },
    {
      id: AccountActionId.APPEARANCE,
      label: 'Appearance',
      section: AccountActionSection.USER,
      kind: AccountActionKind.APPEARANCE,
      surfaces: [AccountMenuSurface.DESKTOP_USER, AccountMenuSurface.MOBILE_MORE],
    },
    {
      id: AccountActionId.SIGN_OUT,
      label: SIGN_OUT_LABEL,
      section: AccountActionSection.USER,
      kind: AccountActionKind.ACTION,
      surfaces: [AccountMenuSurface.DESKTOP_USER, AccountMenuSurface.MOBILE_MORE],
    },
  );

  return actions;
}

export function getAccountActionSectionOwnership(): ReadonlyMap<
  AccountActionId,
  AccountActionSection
> {
  const ownership = new Map<AccountActionId, AccountActionSection>([
    [AccountActionId.WORKSPACE_SETTINGS, AccountActionSection.WORKSPACE],
    [AccountActionId.BILLING, AccountActionSection.WORKSPACE],
    [AccountActionId.ONBOARDING_SETUP, AccountActionSection.WORKSPACE],
    [AccountActionId.OPEN_HELP, AccountActionSection.HELP],
    [AccountActionId.DOCUMENTATION, AccountActionSection.HELP],
    [AccountActionId.LIVE_CHAT, AccountActionSection.HELP],
    [AccountActionId.API_KEYS, AccountActionSection.DEVELOPER],
    [AccountActionId.APPEARANCE, AccountActionSection.USER],
    [AccountActionId.SIGN_OUT, AccountActionSection.USER],
  ]);
  return ownership;
}

export function getActionsForSurface(
  actions: readonly AccountActionDefinition[],
  surface: AccountMenuSurface,
): AccountActionDefinition[] {
  return actions.filter(action => action.surfaces.includes(surface));
}

export function getActionsForSection(
  actions: readonly AccountActionDefinition[],
  section: AccountActionSection,
): AccountActionDefinition[] {
  return actions.filter(action => action.section === section);
}

export function isAppearanceThemeId(value: string): value is AppearanceThemeId {
  return (
    value === AppearanceThemeId.LIGHT ||
    value === AppearanceThemeId.DARK ||
    value === AppearanceThemeId.SYSTEM
  );
}
